import { useState } from "react";
import * as authApi from "../../api/auth";
import { APIConnectionError } from "../../api/errors";
import { STRENGTH_COLORS, passwordStrength, signupProblem } from "../../auth-validation";
import { setLoginPrefill } from "../../state/authPrefill";
import { useAuthConfig } from "../../state/useAuthConfig";
import { PasswordInput } from "../../ui/PasswordInput";
import { AuthCard } from "./AuthCard";
import { TERMS_NOT_AGREED, TermsAgreement } from "./TermsAgreement";

/** signup_window.py */
export function SignupScreen() {
  const [f, setF] = useState({ username: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v });
  const config = useAuthConfig();
  const signupEnabled = config.signup_enabled;
  const [agreed, setAgreed] = useState(false);
  const strength = f.password ? passwordStrength(f.password) : null;

  const submit = async () => {
    const problem = signupProblem(f);
    if (problem) { setError(problem); return; }
    if (config.terms_version && !agreed) { setError(TERMS_NOT_AGREED); return; }
    setBusy(true);
    setError("");
    try {
      const user = await authApi.signup(f.username.trim(), f.email.trim(), f.password, f.confirm, config.terms_version ?? undefined);
      setLoginPrefill(user.username);
      window.location.hash = "#/login";
    } catch (e) {
      setError(e instanceof APIConnectionError ? `Connection error: ${e.detail}` : (e as Error).message);
      setBusy(false);
    }
  };

  if (!signupEnabled) {
    return (
      <AuthCard>
        <h1>Create Account</h1>
        <div className="sub" data-testid="signup-closed">
          Self-registration is closed on this server. Ask an administrator to invite you.
        </div>
        <div className="switch"><a className="btn flat" href="#/login" data-testid="to-login">Back to sign in</a></div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <h1>Create Account</h1>
      <label className="caption" htmlFor="su-user">Username</label>
      <input id="su-user" className="input" placeholder="Choose a username" autoComplete="username" value={f.username} onChange={(e) => set("username")(e.target.value)} autoFocus />
      <label className="caption" htmlFor="su-email">Email</label>
      <input id="su-email" className="input" placeholder="your@email.com" autoComplete="email" value={f.email} onChange={(e) => set("email")(e.target.value)} />
      <label className="caption" htmlFor="su-pw">Password</label>
      <PasswordInput id="su-pw" value={f.password} onChange={set("password")} placeholder="Min 8 characters" autoComplete="new-password" />
      <div className="strength" data-testid="strength" style={{ color: strength ? STRENGTH_COLORS[strength] : undefined, minHeight: 14 }}>{strength ?? ""}</div>
      <label className="caption" htmlFor="su-pw2">Confirm Password</label>
      <PasswordInput id="su-pw2" value={f.confirm} onChange={set("confirm")} placeholder="Repeat password" onEnter={submit} autoComplete="new-password" />
      <TermsAgreement config={config} agreed={agreed} onChange={setAgreed} />
      <div className="error-text" role="alert" data-testid="signup-error">{error}</div>
      <button className="btn primary" disabled={busy} onClick={submit} data-testid="signup-submit">Create Account</button>
      <div className="switch">Already have an account? <a className="btn flat" href="#/login" data-testid="to-login">Sign in</a></div>
    </AuthCard>
  );
}
